import React, { useState } from 'react';
import styled from 'styled-components';
import menuApi from 'api/requests/menu';
import posEventsApi from 'api/requests/posEvents';
import colors from 'theme/colors';
import { panelAccent } from 'theme/panelAccent';

function parseCurrencyInput(raw) {
    const cleaned = raw.replace(/[^\d]/g, '');
    return (parseInt(cleaned, 10) || 0) / 100;
}

function formatCurrencyInput(value) {
    if (!value && value !== 0) return '';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function TicketForm({ slug, posEventId, initialData, onSaved, onCancel }) {
    const [name, setName] = useState(initialData?.name || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [basePrice, setBasePrice] = useState(
        initialData?.base_price != null ? formatCurrencyInput(Number(initialData.base_price)) : ''
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    function handlePriceChange(e) {
        const numeric = parseCurrencyInput(e.target.value);
        setBasePrice(formatCurrencyInput(numeric));
    }

    async function handleSubmit() {
        const price = parseCurrencyInput(basePrice);
        if (!name.trim() || !basePrice) {
            setError('Nome e preço são obrigatórios.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const payload = {
                name: name.trim(),
                description: description.trim() || null,
                base_price: parseCurrencyInput(basePrice),
                type: 'ticket',
                pos_event_id: posEventId,
            };
            let saved;
            if (initialData) {
                const { data } = await menuApi.updateProduct(slug, initialData.id, payload);
                saved = data;
            } else {
                const { data } = await posEventsApi.createTicket(slug, posEventId, payload);
                saved = data;
            }
            onSaved(saved);
        } catch {
            setError('Erro ao salvar. Tente novamente.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Form>
            <FormField>
                <FormLabel>Nome</FormLabel>
                <FormInput
                    placeholder="Ex: Pista - Lote 1"
                    value={name}
                    autoFocus
                    onChange={e => setName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !loading && handleSubmit()}
                />
            </FormField>

            <FormField>
                <FormLabel>Descrição <FormOptional>(opcional)</FormOptional></FormLabel>
                <FormTextarea
                    placeholder="Ex: Acesso à área principal, open bar incluso"
                    value={description}
                    rows={2}
                    onChange={e => setDescription(e.target.value)}
                />
            </FormField>

            <FormField>
                <FormLabel>Preço (R$)</FormLabel>
                <FormInput
                    placeholder="0,00"
                    value={basePrice}
                    inputMode="numeric"
                    onChange={handlePriceChange}
                    onKeyDown={e => e.key === 'Enter' && !loading && handleSubmit()}
                />
            </FormField>

            {error && <ErrorText>{error}</ErrorText>}

            <Actions>
                <CancelBtn type="button" onClick={onCancel} disabled={loading}>
                    Cancelar
                </CancelBtn>
                <SubmitBtn type="button" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Salvando...' : (initialData ? 'Salvar alterações' : 'Criar ingresso')}
                </SubmitBtn>
            </Actions>
        </Form>
    );
}

const Form = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FormLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const FormInput = styled.input`
    height: 44px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 0 14px;
    font-size: 15px;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
    &::placeholder { color: ${colors.nightLight}; }
`;

const FormOptional = styled.span`
    font-size: 13px;
    font-weight: 400;
    color: ${colors.nightLight};
`;

const FormTextarea = styled.textarea`
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 10px 14px;
    font-size: 15px;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;
    resize: vertical;
    font-family: inherit;
    transition: border-color 0.15s;
    &:focus { border-color: ${panelAccent(colors.occasionalPurple)}; }
    &::placeholder { color: ${colors.nightLight}; }
`;

const ErrorText = styled.p`
    font-size: 13px;
    color: ${colors.danger};
    margin: 0;
`;

const Actions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 4px;
`;

const CancelBtn = styled.button`
    flex: 1;
    height: 48px;
    border-radius: 12px;
    border: 1.5px solid ${colors.neutralDark};
    background: transparent;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.night};
    cursor: pointer;
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${colors.neutralDark}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SubmitBtn = styled.button`
    flex: 2;
    height: 48px;
    border-radius: 12px;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
